import { toast } from "react-toastify";
import { GetProductParams } from "store/reducers/products/products.types";
import { Productline } from "types/productline";
import { encodeParams, getUserId } from "./helpers";
import {
  API_ERROR_DEFAULT,
  API_ERROR_NO_SERVER_RESPONSE,
  API_ERROR_SENDING_REQUEST,
} from "./error";
import {
  Ruleset,
  Tag,
  PropertyValueAttribute,
} from "store/reducers/products/parameters/pl-parameters.types";
import {
  PropertyResponse,
  Property,
  PropertyQueryParams,
  PropertyPage,
  PropertyResponsePage,
} from "store/reducers/products/properties/pl-properties.types";
import {
  RuleTable,
  RuleTableResponse,
  RuleTablePage,
  RuleTableResponsePage,
  TablesTypeEnum,
} from "types/table";
import { GetTableSummariesRequest, TableSummary } from "types/tableSummary";
import { newPropertyPriorityList } from "../store/reducers/products/propertyPriorities/pl-propertyPriorities.types";
import { Property as PropertyPP } from "../store/reducers/products/propertyPriorities/pl-propertyPriorities.types";
import axiosInstance from "./interceptor";
import {
  AxiosError,
  AxiosRequestConfig,
  AxiosHeaders,
  AxiosResponse,
} from "axios";
import {
  ExcelExportProcess,
  ExcelImportProcess,
  ProcessBase,
  UpdateUnitSchemaProcess,
  ValidationProcess,
} from "types/process";
import {
  UpdateValidationSettingsRequest,
  Validation,
  ValidationResponse,
  ValidationSettingsResponse,
} from "types/validation";
import {
  ExcelExportRequestParams,
  ExcelImportRequestParams,
} from "types/excel";
import { MacroResponse, ImportTsvAction, ImportTsvMatch } from "types/misc";

class API {
  _getHeaders() {
    //Add the user ID to the headers.  API uses this to determine what
    //productlines the user has permissions to view/modify
    return {
      "X-user-id": getUserId(),
    };
  }

  // private methods
  async _getRequest<T = any>(
    endpoint: string,
    params?: any,
    displayError = true,
    returnError = true,
    axiosConfig: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<T>> {
    try {
      const encodedParams = encodeParams(params);
      const response = await axiosInstance.get<T>(
        endpoint + "?" + encodedParams,
        {
          headers: this._getHeaders(),
          ...axiosConfig,
        }
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError;

      //If we are flagged to display error, display using toast
      if (displayError) {
        if (axiosError.response) {
          // Error response received
          toast.error(axiosError.response.data || API_ERROR_DEFAULT);
        } else if (axiosError.request) {
          // No response received
          toast.error(API_ERROR_NO_SERVER_RESPONSE);
        } else {
          // Error setting up the request
          toast.error(API_ERROR_SENDING_REQUEST + ": " + axiosError.message);
        }
      }

      //If we aren't returning the error throw it
      if (!returnError) throw axiosError;

      // Return the error wrapped in an AxiosResponse<T> to keep our return promise
      return (
        (axiosError.response as AxiosResponse<T> | undefined) ??
        this.buildNoResponse<T>(endpoint, axiosConfig, axiosError)
      );
    }
  }

  async _postRequest<T = any>(
    endpoint: string,
    body: any,
    displayError = true,
    returnError = true,
    axiosConfig: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<T>> {
    try {
      const response = await axiosInstance.post<T>(endpoint, body, {
        headers: this._getHeaders(),
        ...axiosConfig, // merge in extra config like responseType, timeout, etc.
      });

      return response;
    } catch (error) {
      const axiosError = error as AxiosError;

      //If we are flagged to display error, display using toast
      if (displayError) {
        if (axiosError.response) {
          // Error response received
          toast.error(axiosError.response.data || API_ERROR_DEFAULT);
        } else if (axiosError.request) {
          // No response received
          toast.error(API_ERROR_NO_SERVER_RESPONSE);
        } else {
          // Error setting up the request
          toast.error(API_ERROR_SENDING_REQUEST + ": " + axiosError.message);
        }
      }

      //If we aren't returning the error throw it
      if (!returnError) throw axiosError;

      // Return the error wrapped in an AxiosResponse<T> to keep our return promise
      return (
        (axiosError.response as AxiosResponse<T> | undefined) ??
        this.buildNoResponse<T>(endpoint, axiosConfig, axiosError)
      );
    }
  }

  async _putRequest<T = any>(
    endpoint: string,
    body: any,
    displayError = true,
    returnError = true,
    axiosConfig: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<T>> {
    try {
      const response = await axiosInstance.put<T>(endpoint, body, {
        headers: this._getHeaders(),
        ...axiosConfig, // merge in extra config like responseType, timeout, etc.
      });

      return response;
    } catch (error) {
      const axiosError = error as AxiosError;

      //If we are flagged to display error, display using toast
      if (displayError) {
        if (axiosError.response) {
          // Error response received
          toast.error(axiosError?.response?.data || API_ERROR_DEFAULT);
        } else if (axiosError.request) {
          // No response received
          toast.error(API_ERROR_NO_SERVER_RESPONSE);
        } else {
          // Error setting up the request
          toast.error(API_ERROR_SENDING_REQUEST + ": " + axiosError.message);
        }
      }

      //If we aren't returning the error throw it
      if (!returnError) throw axiosError;

      // Return the error wrapped in an AxiosResponse<T> to keep our return promise
      return (
        (axiosError.response as AxiosResponse<T> | undefined) ??
        this.buildNoResponse<T>(endpoint, axiosConfig, axiosError)
      );
    }
  }

  async _deleteRequest<T = any>(
    endpoint: string,
    body?: any,
    params?: any,
    displayError = true,
    returnError = false,
    axiosConfig: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<T>> {
    try {
      const encodedParams = encodeParams(params);
      const response = await axiosInstance.delete<T>(
        endpoint + "?" + encodedParams,
        {
          headers: this._getHeaders(),
          data: body,
          ...axiosConfig, // merge in extra config like responseType, timeout, etc.
        }
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError;

      //If we are flagged to display error, display using toast
      if (displayError) {
        if (axiosError.response) {
          // Error response received
          toast.error(axiosError?.response?.data || API_ERROR_DEFAULT);
        } else if (axiosError.request) {
          // No response received
          toast.error(API_ERROR_NO_SERVER_RESPONSE);
        } else {
          // Error setting up the request
          toast.error(API_ERROR_SENDING_REQUEST + ": " + axiosError.message);
        }
      }

      //If we aren't returning the error throw it
      if (!returnError) throw axiosError;

      // Return the error wrapped in an AxiosResponse<T> to keep our return promise
      return (
        (axiosError.response as AxiosResponse<T> | undefined) ??
        this.buildNoResponse<T>(endpoint, axiosConfig, axiosError)
      );
    }
  }

  async _putFile(endpoint: string, file: Blob) {
    const imgBody = new FormData();
    imgBody.append("file", file, "cover-image.png");

    return axiosInstance.put(endpoint, imgBody, {
      headers: {
        ...this._getHeaders(),
        "Content-Type": "multipart/form-data",
      },
    });
  }

  //Build an AxiosResponse<T> that wraps a no server response error
  private buildNoResponse<T>(
    endpoint: string,
    axiosConfig: AxiosRequestConfig,
    axiosError: AxiosError
  ): AxiosResponse<T> {
    return {
      data: API_ERROR_NO_SERVER_RESPONSE as unknown as T,
      status: 0,
      statusText: "NO_RESPONSE",
      headers: {},
      config: {
        ...axiosConfig,
        url: endpoint,

        headers: AxiosHeaders.from({
          ...(axiosConfig.headers ?? {}),
          ...this._getHeaders(),
        }),
      },
      request: axiosError.request,
    };
  }

  // application API
  async getApplicationInfo(): Promise<any> {
    return this._getRequest("/Application");
  }

  // products API
  async getProducts(params?: GetProductParams) {
    return this._getRequest("/productlines", params);
  }

  async createProduct(productLine: Partial<Productline>) {
    return this._postRequest("/productlines", productLine);
  }

  async addOrUpdateCoverImageProductLine(coverImage: Blob, productLineId) {
    return this._putFile(
      `/ProductLines/${productLineId}/Cover-Image`,
      coverImage
    );
  }

  async getProductLineImage(productLineId: string): Promise<any> {
    return this._getRequest(`/ProductLines/${productLineId}/Cover-Image`);
  }

  async getProductLine(
    id: string,
    displayError = true
  ): Promise<AxiosResponse<any>> {
    return this._getRequest(`/productlines/${id}`, null, displayError);
  }

  async updateProductLine(id: string, body: any) {
    return this._putRequest(`/productlines/${id}`, body);
  }

  async deleteProductLine(id: string) {
    return this._deleteRequest(`/productlines/${id}`);
  }

  async addUserToProductLine(productLineId, userID: string) {
    return this._putRequest(`/productlines/${productLineId}/Users`, {
      Id: userID.toString(),
    });
  }

  async updateProductLineUser(
    productLineId: string,
    userId: string,
    body: any
  ): Promise<any> {
    return this._putRequest(
      `/productlines/${productLineId}/users/${userId}`,
      body
    );
  }

  async removeUserFromProductLine(productLineId, userID: string) {
    return this._deleteRequest(`/productlines/${productLineId}/Users`, {
      Id: userID,
    });
  }

  async getProductLinePVA(productLineId) {
    return this._getRequest(
      `/productlines/${productLineId}/properties-value-attributes`
    );
  }

  async createProductLinePVA(
    pva: Partial<PropertyValueAttribute>,
    productLineId: string
  ) {
    return this._postRequest(
      `/productlines/${productLineId}/properties-value-attributes`,
      pva
    );
  }

  async createStandardPVAs(productLineId) {
    return this._postRequest(
      `/productlines/${productLineId}/properties-value-attributes/CreateStandardPVAs`,
      null
    );
  }

  async changeProductLinePVA(
    { name, dataType, id }: PropertyValueAttribute,
    productLineId: string
  ) {
    return this._putRequest(
      `/productlines/${productLineId}/properties-value-attributes/${id}`,
      { name, dataType }
    );
  }

  async deleteProductLinePVA(productLineId, propertyValueAttributeId) {
    return this._deleteRequest(
      `/productlines/${productLineId}/properties-value-attributes/${propertyValueAttributeId}`
    );
  }

  async createProductLineRuleset(ruleSet: Partial<Ruleset>, productLineId) {
    return this._postRequest(
      `/productlines/${productLineId}/ruleSets`,
      ruleSet
    );
  }

  async deleteProductLineRuleset(productLineId: string, ruleSetId: string) {
    return this._deleteRequest(
      `/productlines/${productLineId}/ruleSets/${ruleSetId}`
    );
  }

  async changeProductLineRuleset(
    { name, description, id }: Ruleset,
    productLineId: string
  ) {
    return this._putRequest(`/productlines/${productLineId}/ruleSets/${id}`, {
      name,
      description,
    });
  }

  async createProductLineTag(tag: Partial<Tag>, productLineId) {
    return this._postRequest(`/productlines/${productLineId}/tags`, tag);
  }

  async createProductLineStandardTags(productLineId) {
    return this._postRequest(
      `/productlines/${productLineId}/tags/CreateStandardTags`,
      null
    );
  }

  async deleteProductLineTag(productLineId: string, tagId: string) {
    return this._deleteRequest(`/productlines/${productLineId}/tags/${tagId}`);
  }

  async getProductLineTags(productLineId) {
    return this._getRequest(`/productlines/${productLineId}/tags`);
  }

  async getProductLineTagsCount(productLineId) {
    return this._getRequest(`/productlines/${productLineId}/tags/count`);
  }

  async changeProductLineTag(
    { name, description, category, id }: Tag,
    productLineId: string
  ) {
    return this._putRequest(
      `/productlines/${productLineId}/tags/UpdateWithCount/${id}`,
      {
        name,
        description,
        category,
      }
    );
  }

  async getProductLineRulesets(
    productLineId
  ): Promise<AxiosResponse<Ruleset[]>> {
    return this._getRequest<Ruleset[]>(
      `/productlines/${productLineId}/ruleSets`
    );
  }

  async exportProductLineRuleEngineJson(
    productLineId,
    sortedOutput = false
  ): Promise<any> {
    return this._getRequest(
      `/productlines/${productLineId}/ExportRuleEngineJson`,
      { sortedOutput }
    );
  }

  async exportProductLineDatabaseJson(productLineId): Promise<any> {
    return this._getRequest(
      `/productlines/${productLineId}/ExportDatabaseJson`
    );
  }

  // Excel

  //Export productline data to Excel
  //In this version, the artifacts to export are specified in the
  //exportParams parameter.
  async exportToExcel(
    productLineId: string,
    exportParams: ExcelExportRequestParams
  ): Promise<AxiosResponse<ExcelExportProcess>> {
    return this._postRequest<ExcelExportProcess>(
      `/productlines/${productLineId}/excel/export`,
      exportParams,
      true,
      false
    );
  }

  //Retrieve the file from a previous Excel Export call.
  async getExcelExport(
    productLineId: string,
    fileName: string
  ): Promise<AxiosResponse<Blob>> {
    return this._getRequest<Blob>(
      `/productlines/${productLineId}/excel/export`,
      { fileName },
      true, // displayError
      true, // returnError
      { responseType: "blob" }
    );
  }

  //Excel Import

  /**
   * Import productline data from Excel.
   * Multipart payload: file + JSON request params.
   */
  // async importFromExcel(
  //   productLineId: string,
  //   file: File,
  //   importParams: ExcelImportRequestParams
  // ): Promise<AxiosResponse<ExcelImportProcess>> {
  //   const form = new FormData();
  //   form.append("file", file, file.name);
  //   // match your server binder: commonly "request" or "params"
  //   form.append(
  //     "request",
  //     new Blob([JSON.stringify(importParams)], { type: "application/json" })
  //   );

  //   // If your _postRequest already detects FormData and sets headers,
  //   // this works as-is. Otherwise, use your multipart helper if you have one
  //   // (e.g., _postMultipartRequest) or set the content-type in options.
  //   return this._postRequest<ExcelImportProcess>(
  //     `/productlines/${productLineId}/excel/import`,
  //     form,
  //     true,
  //     false
  //   );
  // }

  /**
   * Import productline data from Excel.
   * Conforms to ExcelImportController.Import_QueueProcess (multipart/form-data).
   *
   * Fields:
   *  - file: .xlsx workbook (required)
   *  - WorksheetNames: repeated string fields (optional)
   *  - ImportProperties: boolean
   *  - ImportTables: boolean
   *  - ConflictAction: enum string (Abort|New|Replace)
   */
  async importFromExcel(
    productLineId: string,
    file: File,
    importParams: ExcelImportRequestParams
  ): Promise<AxiosResponse<ExcelImportProcess>> {
    const form = new FormData();

    // File (required)
    form.append("file", file, file.name);

    // WorksheetNames: repeat the field for each name if provided
    if (importParams.worksheetNames && importParams.worksheetNames.length > 0) {
      for (const name of importParams.worksheetNames) {
        // server validates non-empty names
        if (name && name.trim().length > 0) {
          form.append("WorksheetNames", name.trim());
        }
      }
    } else {
      // If null/undefined, omit the field entirely to mean "import all"
    }

    // Boolean flags (controller expects form fields, not JSON)
    form.append("ImportProperties", String(importParams.importProperties));
    form.append("ImportTables", String(importParams.importTables));

    // Enum as string (Abort | New | Replace)
    form.append("ConflictAction", String(importParams.conflictAction));

    // Optional flag present in your TS model (controller reads it during import)
    // If you need it honored by the server, and the model binder supports it as a form field,
    // include it; otherwise omit. The controller's ValidateRequest() doesn't check it,
    // but ImportAsync likely consumes it.
    form.append(
      "DeleteUnmatchedSubproperties",
      String(importParams.deleteUnmatchedSubproperties)
    );

    // NOTE:
    // - Controller route: /productlines/{productLineId}/excel/import
    // - It returns 202 Accepted with the ExcelImportProcess on success.
    // - It requires a user id header (e.g., X-User-Id). Ensure your Axios instance or _postRequest
    //   sets this header as the backend expects.
    // - Only .xlsx is allowed; client-side should already validate.
    // Controller reference: ExcelImportController.Import_QueueProcess
    // (Consumes multipart/form-data; binds file + form fields).
    // Source: backend file you shared.
    //
    // _postRequest<T>(url, data, includeAuth, showLoading) should pass FormData as-is.
    return this._postRequest<ExcelImportProcess>(
      `/productlines/${productLineId}/excel/import`,
      form,
      true,
      false
    );
  }

  async importProductLineDatabaseJson(
    file: any,
    name: string,
    region: string,
    conflictAction: string
  ): Promise<any> {
    //Create a form data object containing the file
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    formData.append("region", region);
    formData.append("conflictAction", conflictAction);

    return this._postRequest(
      `/productlines/ImportDatabaseJson`,
      formData,
      true,
      true
    );
  }

  async importProductLineRuleEngineJson(
    file: any,
    name: string,
    region: string
  ): Promise<any> {
    //Create a form data object containing the file
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", name);
    formData.append("region", region);

    return this._postRequest(
      `/productlines/ImportRuleEngineJson`,
      formData,
      true,
      true
    );
  }

  async validateProductLine(productLineId): Promise<any> {
    return this._postRequest(`/productlines/${productLineId}/validate`, null);
  }

  /**
   * Retrieves a lightweight version of a specific property for a product line.
   *
   * @param productLineId - The ID of the product line
   * @param propertyId - The ID of the property to retrieve
   *
   * @returns Promise containing the lightweight property data
   */
  async getPropertyLight(
    productLineId: string,
    propertyId: string
  ): Promise<AxiosResponse<Property>> {
    return this._getRequest(
      `/productlines/${productLineId}/properties/${propertyId}/light`
    );
  }

  // products/properties
  async getProperties(
    productLineId: string,
    name?: string,
    dataTypes?: any,
    tags?: any,
    ruleSets?: any,
    domains?: any,
    includePVAProperties?: boolean,
    includeSubProperties?: boolean,
    items?: number,
    page?: number,
    exclusiveRulesetFilter?: boolean,
    regexNameMatch?: boolean
  ): Promise<AxiosResponse<PropertyResponsePage>> {
    return this._getRequest<PropertyResponsePage>(
      `/productlines/${productLineId}/properties`,
      {
        name,
        dataTypes,
        tags,
        ruleSets,
        domains,
        includePVAProperties,
        includeSubProperties,
        items,
        page,
        exclusiveRulesetFilter,
        regexNameMatch,
      }
    );
  }

  /**
   * Retrieves lightweight properties for a product line with optional filtering.
   *
   * @param productLineId - The ID of the product line
   * @param name - Optional filter by property name
   * @param dataTypes - Optional filter by data types
   * @param tags - Optional filter by tags
   * @param ruleSets - Optional filter by rule sets
   * @param domains - Optional filter by domains
   * @param includePVAProperties - Whether to include PVA properties
   * @param includeSubProperties - Whether to include sub-properties
   * @param items - Number of items per page
   * @param page - Page number for pagination
   * @param exclusiveRulesetFilter - Whether to use exclusive ruleset filtering
   * @param regexNameMatch - Whether to use regex for name matching
   *
   * @returns Promise containing the lightweight properties data
   */
  async getPropertiesLight(
    productLineId: string,
    query: PropertyQueryParams
  ): Promise<AxiosResponse<PropertyPage>> {
    return this._getRequest(
      `/productlines/${productLineId}/properties/light`,
      query
    );
  }

  async getPLSubProperties(
    productLineId: string,
    PLPropertyId: string
  ): Promise<any> {
    return this._getRequest(
      `/productlines/${productLineId}/properties/${PLPropertyId}/sub-properties`
    );
  }

  // products/properties
  async getProductLineProperty(productLineId: string, propertyId: string) {
    return this._getRequest(
      `/productlines/${productLineId}/properties/${propertyId}`
    );
  }

  async createProperty(
    property: Property,
    productLineId: string
  ): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/properties`,
      property
    );
  }

  async createOrReplaceProperty(
    property: Property,
    productLineId: string
  ): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/properties/createOrReplace`,
      property
    );
  }

  async cloneProperty(
    productLineId: string,
    propertyId: string,
    newName: string
  ): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/properties/${propertyId}/clone`,
      { newName }
    );
  }

  async createValidSubproperty(
    productLineId: string,
    propertyId: string
  ): Promise<AxiosResponse<PropertyResponse>> {
    return this._postRequest(
      `/productlines/${productLineId}/properties/${propertyId}/create-valid-subproperty`,
      null
    );
  }

  async editProperty(
    productLineId,
    propertyId,
    property: PropertyResponse
  ): Promise<any> {
    return this._putRequest(
      `/productlines/${productLineId}/properties/${propertyId}`,
      property
    );
  }

  async addPvaRowToProperty(propertyId, pvaRow, productLineId): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/properties/${propertyId}/property-values`,
      pvaRow
    );
  }

  async addPvasToProperty(propertyId, pvas, productLineId): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/properties/${propertyId}/properties-value-attributes`,
      pvas
    );
  }

  async editPropertyPvaCell(
    { propertyId, productLineId, propertyValueId, propertyValueAttributeId },
    newBody
  ): Promise<any> {
    return this._putRequest(
      `/productlines/${productLineId}/properties/${propertyId}/property-values/${propertyValueId}/property-value-attributes/${propertyValueAttributeId}`,
      newBody
    );
  }

  async removePvasFromProperty(propertyId, pvas, productLineId): Promise<any> {
    return this._deleteRequest(
      `/productlines/${productLineId}/properties/${propertyId}/properties-value-attributes`,
      pvas
    );
  }

  async removeProperty(
    productLineId,
    propertyId: string,
    params = { force: false }
  ) {
    return this._deleteRequest(
      `/productlines/${productLineId}/properties/${propertyId}`,
      {},
      params
    );
  }

  async removePropertyValueRowFromProperty(
    productLineId: string,
    propertyId: string,
    propertyValueId: string
  ) {
    return this._deleteRequest(
      `/productlines/${productLineId}/properties/${propertyId}/property-values/${propertyValueId}`
    );
  }

  async reOrderPropertyValueRows(
    propertyId,
    productLineId,
    propertyValueId,
    position: number
  ): Promise<any> {
    return this._putRequest(
      `productlines/${productLineId}/properties/${propertyId}/property-values/${propertyValueId}`,
      {
        position,
      }
    );
  }

  async validateProperty(
    productLineId: string,
    propertyId: string
  ): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/properties/${propertyId}/validate`,
      null
    );
  }

  // products/candidates
  async getCandidateSelection(productLineId: string) {
    return this._getRequest(
      `/productlines/${productLineId}/candidate-selection`
    );
  }

  async modifyCandidateSelection(productLineId: string, candidateSel: any) {
    return this._putRequest(
      `/productlines/${productLineId}/candidate-selection`,
      candidateSel
    );
  }

  async createCandidateSelection(productLineId: string) {
    return this._postRequest(
      `/productlines/${productLineId}/candidate-selection`,
      {}
    );
  }

  // products/metadata
  async getMetadata(productLineId: string) {
    return this._getRequest(`/productlines/${productLineId}/meta-data-db`);
  }

  async modifyMetadata(productLineId: string, metadata: any) {
    return this._putRequest(
      `/productlines/${productLineId}/meta-data-db`,
      metadata
    );
  }

  async createMetadata(productLineId: string) {
    return this._postRequest(`/productlines/${productLineId}/meta-data-db`, {});
  }

  //products/rules-tables

  async getRuleTables(
    productLineId: string,
    items: number | string,
    page: number,
    tableTypes?: TablesTypeEnum[],
    rulesetIds?: string[],
    propertyIds?: string[]
  ): Promise<AxiosResponse<RuleTableResponsePage>> {
    return this._getRequest(`/productlines/${productLineId}/rule-tables`, {
      tableTypes,
      rulesetIds,
      propertyIds,
      items,
      page,
    });
  }

  /**
   * Retrieves a lightweight version of rule tables for a product line with optional filtering
   *
   * @param productLineId - The ID of the product line
   * @param items - Number of items per page or 'all'
   * @param page - Page number for pagination
   * @param tableTypes - Optional filter by table types
   * @param rulesetIds - Optional filter by ruleset IDs
   * @param propertyIds - Optional filter by property IDs
   *
   * @returns Promise containing the lightweight rule tables data
   */
  getRuleTablesLight(
    productLineId: string,
    items: number | string,
    page: number,
    tableTypes?: TablesTypeEnum[],
    rulesetIds?: string[],
    propertyIds?: string[]
  ): Promise<AxiosResponse<RuleTablePage>> {
    return this._getRequest<RuleTablePage>(
      `/productlines/${productLineId}/rule-tables/light`,
      {
        tableTypes,
        rulesetIds,
        propertyIds,
        items,
        page,
      }
    );
  }

  /**
   * Retrieves condensed summary metadata for rule tables in a product line.
   *
   * @param productLineId - The ID of the product line
   * @param params - Optional query filters (table types, rulesets, properties, counts, etc.)
   *
   * @returns Promise containing table summaries (row counts, validation counts, resolved names)
   */
  async getTableSummaries(
    productLineId: string,
    params?: GetTableSummariesRequest
  ): Promise<AxiosResponse<TableSummary[]>> {
    return this._getRequest<TableSummary[]>(
      `/productlines/${productLineId}/rule-tables/summaries`,
      params
    );
  }

  async getRuleTable(
    productLineId: string,
    ruletableId: string
  ): Promise<AxiosResponse<RuleTableResponse>> {
    return this._getRequest<RuleTableResponse>(
      `productlines/${productLineId}/rule-tables/${ruletableId}`
    );
  }

  async createProductLineRulesTable(
    rulesTable: RuleTable,
    productLineId: string
  ): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/rule-tables`,
      rulesTable
    );
  }

  async createStandardVALIDLookupTable(
    productLineId: string,
    propertyId: string,
    overwriteTables = false
  ): Promise<AxiosResponse<RuleTableResponse>> {
    return this._postRequest(
      `/productlines/${productLineId}/rule-tables/create-valid-lookup`,
      { propertyId, overwriteTables }
    );
  }

  async deleteRuleTable(productLineId: string, tableId: string) {
    return this._deleteRequest(
      `/productlines/${productLineId}/rule-tables/${tableId}`
    );
  }

  async exportRuleTableToTSV(productLineId: string, tableId: string) {
    return this._getRequest(
      `/productlines/${productLineId}/rule-tables/${tableId}/ExportToTSV`
    );
  }

  async importRuleTableFromTSV(
    productLineId: string,
    file: File,
    importAction: ImportTsvAction
  ) {
    const endpoint = `/productlines/${productLineId}/rule-tables/ImportFromTSV`;
    const form = new FormData();
    form.append("file", file, file.name);
    form.append("importAction", importAction);

    // Not calling the usual _postRequest helper because import table
    // has a dedicated dialog to display errors.
    return axiosInstance.post(endpoint, form, {
      headers: this._getHeaders(),
    });
  }

  async getImportTsvMatch(
    productLineId: string,
    tableId?: string | null,
    compareId?: string | null
  ): Promise<AxiosResponse<ImportTsvMatch>> {
    return this._getRequest<ImportTsvMatch>(
      `/productlines/${productLineId}/rule-tables/import-tsv-match`,
      {
        tableId: tableId || undefined,
        compareId: compareId || undefined,
      },
      false
    );
  }

  async editProductLineRulesTable(
    tableId: string,
    productLineId: string,
    ruleTable: RuleTable
  ): Promise<any> {
    return this._putRequest(
      `/productlines/${productLineId}/rule-tables/${tableId}`,
      ruleTable,
      true,
      false
    );
  }

  async reOrderProductLineRulesTableRows(
    tableId: string,
    productLineId: string,
    rowId: string,
    position: number
  ): Promise<any> {
    return this._putRequest(
      `productlines/${productLineId}/rule-tables/${tableId}/rows/${rowId}`,
      {
        position,
      }
    );
  }

  async editRuleTableCell(
    productLineId: string,
    tableId: string,
    rowId: string,
    cellId: string,
    value: string
  ) {
    return this._putRequest(
      `/productlines/${productLineId}/rule-tables/${tableId}/rows/${rowId}/cells/${cellId}`,
      value
    );
  }

  async addPropertiesRowToRuleTable(
    tableId: string,
    propertiesRow,
    productLineId: string
  ): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/rule-tables/${tableId}/rows`,
      propertiesRow
    );
  }

  async deleteRuleTableRow(
    productLineId: string,
    tableId: string,
    rowId: string
  ) {
    return this._deleteRequest(
      `/productlines/${productLineId}/rule-tables/${tableId}/rows/${rowId}`
    );
  }

  async addNewPropertyToRuleTable(
    tableId: string,
    properties: string,
    productLineId: string
  ): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/rule-tables/${tableId}/properties`,
      properties
    );
  }

  async addNewPropertyPvaToRuleTable(
    productLineId: string,
    tableId: string,
    propertyId: string,
    pvaId: string
  ): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/rule-tables/${tableId}/pva-column`,
      { propertyId, pvaId },
      true,
      false
    );
  }

  async removePropertyFromRuleTable(
    productLineId: string,
    tableId: string,
    propertiesId: string[]
  ): Promise<any> {
    return this._deleteRequest(
      `/productlines/${productLineId}/rule-tables/${tableId}/properties`,
      propertiesId
    );
  }

  async validateRuleTable(
    productLineId: string,
    tableId: string
  ): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/rule-tables/${tableId}/validate`,
      null
    );
  }

  //products/macros
  async renameSubproperty(
    productLineId: string,
    oldSubpropertyName: string,
    newSubpropertyName: string
  ): Promise<AxiosResponse<MacroResponse>> {
    return this._postRequest<MacroResponse>(
      `/productlines/${productLineId}/macro/rename-subproperty`,
      {},
      true,
      true,
      {
        params: {
          originalSubPropertyName: oldSubpropertyName,
          newSubpropertyName: newSubpropertyName,
        },
        timeout: 300000,
      }
    );
  }

  async renamePVA(
    productLineId: string,
    oldPvaName: string,
    newPvaName: string
  ): Promise<AxiosResponse<MacroResponse>> {
    return this._postRequest<MacroResponse>(
      `/productlines/${productLineId}/macro/rename-pva`,
      {},
      true,
      true,
      {
        params: {
          originalPvaName: oldPvaName,
          newPvaName: newPvaName,
        },
        timeout: 300000,
      }
    );
  }

  async addPriceKeySubproperties(
    productLineId
  ): Promise<AxiosResponse<MacroResponse>> {
    return this._postRequest<MacroResponse>(
      `/productlines/${productLineId}/macro/add-pricekey-subproperty`,
      {},
      true,
      true,
      {
        timeout: 600000,
      }
    );
  }

  async addPriceKeyPVAs(productLineId): Promise<AxiosResponse<MacroResponse>> {
    return this._postRequest<MacroResponse>(
      `/productlines/${productLineId}/macro/add-pricekey-pva`,
      {},
      true,
      true,
      {
        timeout: 600000,
      }
    );
  }

  async addPriceKeySubpropertyLookupColumns(
    productLineId
  ): Promise<AxiosResponse<MacroResponse>> {
    return this._postRequest<MacroResponse>(
      `/productlines/${productLineId}/macro/add-pricekey-subproperty-lookups`,
      {},
      true,
      true,
      {
        timeout: 600000,
      }
    );
  }

  async addPriceKeyPVALookupColumns(
    productLineId
  ): Promise<AxiosResponse<MacroResponse>> {
    return this._postRequest<MacroResponse>(
      `/productlines/${productLineId}/macro/add-pricekey-pva-lookups`,
      {},
      true,
      true,
      {
        timeout: 600000,
      }
    );
  }

  async updateUnitSchema(
    productLineId: string
  ): Promise<AxiosResponse<UpdateUnitSchemaProcess>> {
    return this._postRequest<UpdateUnitSchemaProcess>(
      `/productlines/${productLineId}/macro/update-unit-schema`,
      {},
      true,
      true,
      {}
    );
  }

  //*** Validation API */

  async getValidations(
    productLineId: string,
    objectId = "",
    objectAssigned = ""
  ): Promise<AxiosResponse<ValidationResponse[]>> {
    return this._getRequest(`/productlines/${productLineId}/validations`, {
      objectId,
      objectAssigned,
    });
  }

  async addValidation(
    productLineId: string,
    body: Validation
  ): Promise<AxiosResponse<Validation>> {
    return this._postRequest(`productlines/${productLineId}/validations`, body);
  }

  async removeValidation(
    productLineId: string,
    validationId: string
  ): Promise<AxiosResponse<void>> {
    return this._deleteRequest(
      `productlines/${productLineId}/validations/${validationId}`
    );
  }

  async validateAllAsync(
    productLineId: string
  ): Promise<AxiosResponse<ValidationProcess>> {
    return this._postRequest(
      `/productlines/${productLineId}/validations/validateAllAsync`,
      null
    );
  }

  async getValidationSettings(
    productLineId: string
  ): Promise<AxiosResponse<ValidationSettingsResponse>> {
    return this._getRequest(
      `/productlines/${productLineId}/validations/settings`
    );
  }

  async updateValidationSettings(
    productLineId: string,
    body: UpdateValidationSettingsRequest
  ): Promise<AxiosResponse<ValidationSettingsResponse>> {
    return this._putRequest(
      `/productlines/${productLineId}/validations/settings`,
      body
    );
  }

  // property priorities

  async getPropertyPrioritiesList(productLineId: string): Promise<any> {
    return this._getRequest(
      `/productlines/${productLineId}/property-priorities`
    );
  }

  async addPropertyPrioritiesList(
    productLineId: string,
    propertyPriorityListObject: newPropertyPriorityList
  ): Promise<any> {
    return this._postRequest(
      `/productlines/${productLineId}/property-priorities`,
      propertyPriorityListObject
    );
  }

  async addPropertiesToPropertyPriorityList(
    productLineId: string,
    propertyPrioritiesId: string,
    properties
  ): Promise<any> {
    return this._postRequest(
      `productlines/${productLineId}/property-priorities/${propertyPrioritiesId}/filtering-properties`,
      properties
    );
  }

  async removePropertyPrioritiesList(
    productLineId: string,
    propertyPriorityListId
  ): Promise<any> {
    return this._deleteRequest(
      `/productlines/${productLineId}/property-priorities/${propertyPriorityListId}`
    );
  }

  async editPropertyInPriorityList(
    productLineId: string,
    propertyPrioritiesId: string,
    editPropertyObject: Partial<PropertyPP>
  ): Promise<any> {
    return this._putRequest(
      `productlines/${productLineId}/property-priorities/${propertyPrioritiesId}/filtering-properties`,
      editPropertyObject
    );
  }

  async editPropertyPriorityList(
    productLineId: string,
    propertyPrioritiesId: string,
    newPropertyList: any
  ): Promise<any> {
    return this._putRequest(
      `productlines/${productLineId}/property-priorities/${propertyPrioritiesId}`,
      newPropertyList
    );
  }

  async removePropertyInPriorityList(
    productLineId: string,
    propertyPrioritiesId: string,
    propertyIdsToRemove: string[]
  ): Promise<any> {
    return this._deleteRequest(
      `productlines/${productLineId}/property-priorities/${propertyPrioritiesId}/filtering-properties`,
      propertyIdsToRemove
    );
  }

  // logic groups

  async getLogicGroups(
    productLineId: string,
    parentLogicGroupId?: string
  ): Promise<any> {
    return this._getRequest(`productlines/${productLineId}/logic-groups`, {
      parentLogicGroupId,
    });
  }

  async getLogicGroup(
    productLineId: string,
    logicGroupId: string
  ): Promise<any> {
    return this._getRequest(`productlines/${productLineId}/${logicGroupId}`);
  }

  async createLogicGroup(
    productLineId: string,
    newLogicGroup: any
  ): Promise<any> {
    return this._postRequest(
      `productlines/${productLineId}/logic-groups`,
      newLogicGroup
    );
  }

  // Processes

  async getProcess(processId: string): Promise<AxiosResponse<ProcessBase>> {
    return this._getRequest(`/processes/${processId}`);
  }

  // programs API
  // ...

  // notifications API
  // ...
}

export const api = new API();
